import { Box } from "@mui/material";
import type {
	CellClassParams,
	CellStyle,
	ColDef,
	GridApi,
	GridReadyEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { globalDefaultColDef, mesfColors } from "@/setup/ag-grid/mesf-theme";
import {
	SUMMARY_PROPERTIES,
	type SummaryPropertyDef,
} from "../models/InformationRetrieval";
import type { SummaryValues } from "../utils/aggregate";

const DASH = "--------";

const formatValue = (
	value: number | null | undefined,
	format: SummaryPropertyDef["format"],
): string => {
	if (value === undefined || value === null) return DASH;
	if (format === "PERCENT") return `${Number(value).toFixed(0)}%`;
	if (format === "FLOAT2") return Number(value).toFixed(2);
	return Number(value).toFixed(3);
};

interface SummaryGridRow {
	propertyKey: string;
	property: string;
	description: string;
	legendColor?: string;
	format: SummaryPropertyDef["format"];
	values: Record<string, number | null | undefined>;
}

const PropertyHeaderRenderer = ({ data }: { data?: SummaryGridRow }) => {
	if (!data) return null;
	return (
		<Box
			title={data.description}
			sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%" }}
		>
			{data.legendColor ? (
				<Box
					component="span"
					sx={{
						width: 10,
						height: 10,
						borderRadius: "50%",
						backgroundColor: data.legendColor,
						border: "1px solid #ACA8A8",
						flexShrink: 0,
					}}
				/>
			) : null}
			<Box
				component="span"
				sx={{ fontWeight: 600, fontSize: 12, color: mesfColors.text.primary }}
			>
				{data.property}
			</Box>
		</Box>
	);
};

const cellStyleCb = (params: CellClassParams<SummaryGridRow>): CellStyle => {
	const base: CellStyle = {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	};
	const stand = params.colDef.colId;
	if (!stand || !params.data) return base;
	// A stand the selection never produced reads as off, same as Trend Display.
	if (params.data.values[stand] === undefined) {
		return { ...base, backgroundColor: "#D6D4D4", color: "#1e293b" };
	}
	return base;
};

interface Props {
	summary: SummaryValues;
	stands: string[];
	/** Rows to show, in order. Defaults to the mill's eight. */
	properties?: readonly SummaryPropertyDef[];
	/** Header of the pinned column. */
	columnLabel?: string;
	onGridReady?: (api: GridApi) => void;
}

export const SummaryGrid = ({
	summary,
	stands,
	properties = SUMMARY_PROPERTIES,
	columnLabel = "Stand",
	onGridReady,
}: Props) => {
	const rowData = useMemo<SummaryGridRow[]>(
		() =>
			properties.map((prop) => ({
				propertyKey: prop.key,
				property: prop.label,
				description: prop.description,
				legendColor: prop.legendColor,
				format: prop.format,
				values: summary[prop.key],
			})),
		[summary, properties],
	);

	const columnDefs = useMemo<ColDef<SummaryGridRow>[]>(() => {
		const propertyCol: ColDef<SummaryGridRow> = {
			colId: "property",
			headerName: columnLabel,
			pinned: "left",
			width: 150,
			minWidth: 150,
			maxWidth: 150,
			sortable: false,
			resizable: false,
			suppressMovable: true,
			// The renderer draws the legend dot, but Excel export reads the value,
			// so the label has to be reachable as one too.
			valueGetter: (params) => params.data?.property,
			cellRenderer: PropertyHeaderRenderer,
			cellStyle: { paddingLeft: 8, display: "flex", alignItems: "center" },
			headerStyle: { fontWeight: 700, fontSize: 13 },
		};

		const standCols = stands.map<ColDef<SummaryGridRow>>((name) => ({
			colId: name,
			headerName: name,
			valueGetter: (params) => params.data?.values[name],
			valueFormatter: (params) =>
				formatValue(
					params.value as number | null | undefined,
					params.data?.format ?? "FLOAT3",
				),
			flex: 1,
			minWidth: 76,
			sortable: false,
			resizable: false,
			cellStyle: cellStyleCb,
			headerStyle: { fontWeight: 700, fontSize: 13 },
		}));

		return [propertyCol, ...standCols];
	}, [stands, columnLabel]);

	if (stands.length === 0) return null;

	return (
		<AgGridReact<SummaryGridRow>
			rowData={rowData}
			columnDefs={columnDefs}
			defaultColDef={{ ...globalDefaultColDef, resizable: false }}
			domLayout="autoHeight"
			headerHeight={28}
			rowHeight={26}
			getRowId={(params) => params.data.propertyKey}
			onGridReady={(event: GridReadyEvent) => onGridReady?.(event.api)}
			suppressCellFocus
			suppressContextMenu
			suppressColumnVirtualisation
			suppressHorizontalScroll
			suppressRowHoverHighlight
			animateRows={false}
		/>
	);
};
